import Component from '@glimmer/component';
import { reads } from '@ember/object/computed';
import { computed, action } from '@ember/object';
import { tracked } from '@glimmer/tracking';

import { toTeller } from 'mewe/fetchers/fetch-stories';
import { TellerType } from 'mewe/constants';
import { showNewToMeWePopup } from 'mewe/utils/dialogs-common';
import MiscUtils from 'mewe/utils/miscellaneous-utils';
import { inject as service } from '@ember/service';
import { next } from '@ember/runloop';
import { installDropdown } from 'mewe/utils/dropdown-utils';

export default class MwGroupMain extends Component {
  @service dynamicDialogs;
  @service tasks;

  @reads('args.rootEvent') rootEvent;
  @reads('args.group') group;
  @reads('args.page') page;
  @reads('args.isOpenPage') isOpenPage;

  @tracked entries = this.args.entries;
  @tracked isAvatarClicked = false;
  @tracked isAvatarDropdownOpen;
  @tracked isStoryCreated = false;

  constructor() {
    super(...arguments);
    installDropdown(this, 'isAvatarDropdownOpen');
  }

  get pagePublicUrl() {
    return this.page.publicUrl.replace('/p/', '/');
  }
  get downloadQrCode() {
    return this.page.publicUrl + '/qr';
  }

  getWindowFeatures() {
    return (
      'status=no,height=330,width=700,resizable=yes,left=' +
      (window.screen.width / 2 - 300) +
      ',top=' +
      (window.screen.height / 2 - 165)
    );
  }

  get pagePublicUrl() {
    return this.page.publicUrl.replace('/p/', '/');
  }

  @computed('rootEvent.name', 'group.name', 'page.name')
  get communityName() {
    if (this.rootEvent) {
      return this.rootEvent.name;
    } else if (this.group) {
      return this.group.name;
    } else if (this.page) {
      return this.page.name;
    }
  }

  @computed('rootEvent.name', 'group.name', 'page.name')
  get communityCountText() {
    if (this.rootEvent) {
      return false;
    } else if (this.group) {
      if (this.group.membersCount === 0) return '';

      return __('{count} Members', { count: this.group.membersCount });
    } else if (this.page) {
      return __('{count} Followers', { count: this.page.followers });
    }
  }

  @computed('rootEvent.description', 'group.descriptionPlain', 'page.description')
  get descriptionPlain() {
    if (this.rootEvent) {
      return this.rootEvent.description;
    } else if (this.group) {
      return this.group.descriptionPlain;
    } else if (this.page) {
      return this.page.description;
    }
  }

  get avatarClass() {
    if (this.isStoryCreated) {
      return 'h-halo--unseen h-halo--w3';
    }

    let value = '';

    if (this.entries?.items?.length) {
      const hasUnseen = this.entries.items.filterBy('isNew', true).length > 0;
      if (hasUnseen) {
        value += this.page.isFollower ? 'h-halo--unseen' : 'h-halo--unseen-unfollowed';
      } else {
        value += 'h-halo--seen';
      }

      value += ' h-halo--w3';
    }

    return value;
  }

  get showAddStoryButton() {
    return this.page && (this.page.isOwner || this.page.isAdmin) && !this.isOpenPage && this.page.published;
  }

  @action
  pageUpdated() {
    this.isStoryCreated = false;
    this.entries = this.args.entries;
  }

  @action
  avatarClick() {
    if (this.isOpenPage && (!this.page.isNsfw || (this.isAvatarClicked && this.page.isNsfw))) {
      return showNewToMeWePopup(this.dynamicDialogs);
    }

    if (this.page.isNsfw && this.isAvatarClicked === false) {
      this.isAvatarClicked = true;
      return;
    }

    // if there is any story then open dropdown, otherwise open avatar in MD
    if (this.entries?.items?.length || this.isStoryCreated) {
      this.setDropdownRef?.(true);
    } else {
      this.dynamicDialogs.openDialog('media-dialog', {
        photoUrl: this.page?.avatar,
        mediaType: 'photoUrl',
        allowMultipleInstances: true,
      });
    }
  }

  @action
  openProfileStories() {
    next(() => {
      this.setDropdownRef?.(false);
      this.isStoryCreated = false;
    });

    const tellerData = toTeller({
      id: this.page.id,
      storytellerType: TellerType.PAGE,
    });
    tellerData.pageStoryteller = this.page; // page is EmberObject, can't be serialized

    this.dynamicDialogs.openDialog('stories-dialog', { tellerData });
  }

  @action
  openProfilePhoto() {
    next(() => {
      this.setDropdownRef?.(false);
    });
    this.dynamicDialogs.openDialog('media-dialog', {
      photoUrl: this.page?.avatar,
      mediaType: 'photoUrl',
      allowMultipleInstances: true,
    });
  }

  @action
  copyToClipboard(text) {
    MiscUtils.copyToClipboard(text);
  }

  @action
  inviteFromFacebook() {
    window.open(
      'https://www.facebook.com/sharer/sharer.php?mode=message&u=' + this.pagePublicUrl,
      'sharer',
      this.getWindowFeatures()
    );
  }

  @action
  inviteFromTwitter() {
    let text = encodeURIComponent(
        __("See what I'm really sharing these days. Join me on MeWe - the social network with privacy!")
      ),
      url = encodeURIComponent(this.pagePublicUrl);

    window.open(
      'https://www.twitter.com/intent/tweet?text=' + text + '&url=' + url,
      'sharer',
      this.getWindowFeatures()
    );
  }

  @action
  addStory() {
    const choosePhotoArgs = {
      task: this.tasks.getTask('photo.story'),
      useNewCropper: true,
      pageId: this.page.id,
      onStoryCreated: () => {
        this.isStoryCreated = true;
      },
    };
    this.dynamicDialogs.openDialog('pick-photo-dialog', choosePhotoArgs);
  }
}
